import type { ChildrenOnly } from '@studio/design-system/props';
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import type { Protocol as ControlProtocol } from '@studio/models/control';
import type { Brand, Model } from '@studio/models/device';
import type { Protocol as VideoProtocol } from '@studio/models/video';
import type { Protocol as AudioProtocol } from '@studio/models/audio';
import type * as S from '@studio/react-utils/store';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import type { UseBoundStoreWithEqualityFn } from 'zustand/traditional';
import type { Candidate, Device, Status } from '#~/device/index.js';
import type { Template } from '#~/device/registry/index.js';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { DeviceSetting } from '@tryiris-ai/common.protojs/agent/v1/device_settings/device_settings';
import type { DeviceRegistryInterfaceSectionType } from '@tryiris-ai/common.protojs/api/v1/device_settings';

/** Mutable data draft. */
export type Draft = S.Draft<State>;

/** Immer producer. */
export type Producer = S.Producer<State>;

/** Iris-Connection sub-tab — re-exported here so callers don't need to
 *  reach into the iris-connection module just to type a deep-link. */
export type IrisConnectionTab = 'monitoring' | 'driver' | 'video' | 'control' | 'authentication';

/** `<Provider>` properties. */
export type Props = ChildrenOnly & {
  agentId: Agent['id'];
  device: Device;
  status: Status;

  /**
   * Room the settings dialog was opened against (Multi-Add Phase 2 fix).
   * Threaded `useSettings` → `SettingsWrapper` → `Settings` → here so the
   * store carries the room. A transitory Iris placeholder has no joined
   * embedded agent yet, so the Authentication tab cannot derive roomId from
   * `agent.roomId`; it falls back to this. Optional: settings opened from
   * the account path have no room context and simply omit it.
   * Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  roomId?: Room['id'];

  /** Replace the open editor's placeholder after a successful camera commit. */
  onDeviceCommitted?: (device: Device) => void;

  /**
   * Initial panel to open. Defaults to `Panel.IrisConnection`. The
   * deep-link prop chain: Settings.defaultPanel →
   * Provider.defaultPanel → store.panel.
   */
  defaultPanel?: Panel;

  /**
   * Initial Iris-Connection sub-tab to open. Chip click flows
   * targetTab here so the Authentication / Video / Control sub-tab is
   * pre-selected when the dialog opens. Read by `<Scaffold>` and
   * passed through to `<IrisConnectionSection defaultTab=...>`.
   */
  defaultTab?: IrisConnectionTab;
};

/** Store state. */
export type State = Readonly<{
  /** Agent ID. */
  agentId: Agent['id'];

  /**
   * Room the dialog was opened against, or `undefined` when settings were
   * opened outside a room (account path). Read by the Authentication tab as
   * the roomId fallback for a transitory Iris placeholder whose `agent` is
   * not yet joined (Multi-Add Phase 2 — fixes "No room available to
   * authenticate against"). Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  roomId: T_Maybe<Room['id']>;

  onDeviceCommitted: Props['onDeviceCommitted'];

  /** Candidate device. */
  candidate: Candidate;

  nameOverride: T_Maybe<string>;

  /** Control panel state. */
  control: {
    /** Cached settings. */
    cache: T_Optional<{ [K in ControlProtocol]: Candidate['control'] }>;
  };

  /** External state. */
  external: {
    status: Status;
  };

  /** A saved configuration remains pending until the camera confirms its connection. */
  verification?: {
    step: 'driver' | 'authentication';
    status: 'pending' | import('../connection-verification.js').ConnectionVerification;
  };

  /**
   * Mounted credentials form, shared by its Submit button and the scoped footer action.
   * `verified`: the typed credentials are the ones its last Submit verified, so the footer finishes.
   */
  authenticationForm?: { id: string; canSubmit: boolean; verified?: boolean } | undefined;

  /** Operation flags. */
  flags: {
    canUndo: boolean;
    isBusy: boolean;
    isDirty: boolean;
    isDeviceSettingsDirty: boolean;
    isVideoSettingsDirty: boolean;
    isNameDirty: boolean;
    /**
     * After a successful model-changing save, move the open tray to
     * Authentication once either aspect reports invalid credentials.
     */
    pendingAuthContinuation: boolean;
  };

  /** Lifecycle copies. */
  lifecycle: {
    /** Original device. */
    original: Device;

    /** Rollback candidate. */
    rollback: T_Maybe<Candidate>;

    /** Candidate snapshot. */
    snapshot: Candidate;
  };

  overrides: Record<string, DeepPartial<DeviceSetting>>;

  /** Manufacturer panel state. */
  manufacturer: {
    /** Device configuration. */
    config: T_Maybe<Template>;

    /** Other configuration indicator. */
    isOther: boolean;

    /** Preferred model ID (per brand). */
    preferredModelId: Record<Brand['id'], T_Maybe<Model['id']>>;
  };

  /** Selected panel. */
  panel: Panel;

  /**
   * Initial Iris-Connection sub-tab from a deep-link, or `undefined`.
   * Read once by `<IrisConnectionSection>` to seed `activeTab`. Stays
   * in the store so post-mount tab changes don't clobber the initial
   * deep-link value (the local `activeTab` is the source of truth
   * after first render).
   */
  defaultTab: T_Maybe<IrisConnectionTab>;

  /**
   * Currently-active Iris-Connection sub-tab (Multi-Add Phase 2).
   *
   * Hoisted to the store so the footer's global Save handler can read
   * which sub-tab the user has open at commit time — used to derive
   * which `dismissedNeedAttentionTabs` slice to write ("Save with
   * edited fields on this tab").
   *
   * Written by `<IrisConnectionSection>`'s `handleTabChange` (and on
   * mount with the initial seed) so the store mirrors the component's
   * local `activeTab`. `undefined` until first write — the footer
   * handler treats undefined as "user never opened the IrisConnection
   * panel during this session", which short-circuits the dismissal
   * write.
   *
   * Per `reference-rules-in-comments` (ai.knowledge/global).
   */
  irisConnectionActiveTab: T_Maybe<IrisConnectionTab>;

  /** Immer producer. */
  produce: Producer;

  /** Video panel state. */
  video: {
    /** Cached configuration. */
    cache: T_Optional<{ [K in VideoProtocol]: Candidate['video'] }>;

    /** Current section. */
    section: string;

    /** Current section key. */
    sectionKey: DeviceRegistryInterfaceSectionType;
  };

  /** Audio panel state. */
  audio: {
    /** Cached configuration. */
    cache: T_Optional<{ [K in AudioProtocol]: Candidate['audio'] }>;

    /** Current section. */
    section: string;
  };

  /** System panel state. */
  system: {
    /** Current section. */
    section: string;
  };

  /** Network panel state. */
  network: {
    /** Current section. */
    section: string;
  };

  /** Features panel state. */
  features: {
    /** Current section. */
    section: string;
  };

  /** Monitoring panel state. */
  monitoring: {
    /** Current section. */
    section: string;
  };

  /** General panel state. */
  general: {
    /** Current section. */
    section: string;
  };

  /** Controls panel state. */
  controls: {
    /** Current section. */
    section: string;
  };

  /** Dynamic section states for arbitrary section groups */
  sectionStates: Record<
    string,
    {
      /** Current section. */
      section: string;
    }
  >;
}>;

/** Workflow store. */
export type Store = UseBoundStoreWithEqualityFn<S.Store<State>>;

/** Workflow panels. */
export enum Panel {
  Audio = 'a',
  Controls = 'c', // the dynamic 'Controls' panel
  Control = 'cntrl', // the pre-defined 'Controls' panel; not dynamic
  IrisConnection = 'irisconn', // container that will house all iris specific connection settings
  Manufacturer = 'm',
  Network = 'n',
  Features = 'f',
  General = 'g',
  Monitoring = 'mnt',
  System = 's',
  Video = 'v', // the dynamic 'Video' panel
  VideoSettings = 'vs' // the pre-defined 'Video' panel; not dynamic
}
